import { useCallback, useEffect, useState } from "react";
import {
  Banner,
  Button,
  Field,
  SimpleTable,
  StatGrid,
  StatTile,
  Switch,
  TextInput,
  useCkToast,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import { aiCreditsApi, type PlatformTenantWalletDto } from "../../../api/aiCreditsApi";
import { errorMessage, fmtCredits, fmtDate, fmtUsd, parseCredits, TX_LABELS } from "./creditsUi";

type Tx = PlatformTenantWalletDto["transactions"][number];

/** Platform (super_admin): an organization's AI wallet — on/off, monthly allowance, grants (ADR-0035). */
export function PlatformAiWalletPanel({ tenantId }: { tenantId: string }) {
  const toast = useCkToast();
  const [data, setData] = useState<PlatformTenantWalletDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [allowance, setAllowance] = useState("");
  const [low, setLow] = useState("");
  const [grant, setGrant] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await aiCreditsApi.platform.wallet(tenantId);
      setData(d);
      setEnabled(d.wallet.enabled);
      setAllowance(d.wallet.monthlyAllowanceCredits === null ? "" : String(d.wallet.monthlyAllowanceCredits));
      setLow(d.wallet.lowBalanceCredits === null ? "" : String(d.wallet.lowBalanceCredits));
    } catch (e) {
      setError(errorMessage(e, "לא הצלחנו לטעון את ארנק ה-AI"));
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    const a = parseCredits(allowance);
    const l = parseCredits(low);
    if (a === undefined || l === undefined) {
      toast.error("ערך לא תקין — מספר שלם או ריק");
      return;
    }
    setBusy(true);
    try {
      await aiCreditsApi.platform.saveWallet(tenantId, { enabled, monthlyAllowanceCredits: a, lowBalanceCredits: l });
      toast.success("נשמר");
      await load();
    } catch (e) {
      toast.error(errorMessage(e, "השמירה נכשלה"));
    } finally {
      setBusy(false);
    }
  };

  const adjust = async () => {
    const t = grant.trim();
    if (!/^-?\d+$/.test(t) || Number(t) === 0) {
      toast.error("סכום לא תקין (אפשר שלילי לתיקון)");
      return;
    }
    setBusy(true);
    try {
      await aiCreditsApi.platform.adjust(tenantId, { credits: Number(t), note: note.trim() || null });
      toast.success("היתרה עודכנה");
      setGrant("");
      setNote("");
      await load();
    } catch (e) {
      toast.error(errorMessage(e, "העדכון נכשל"));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <Banner tone="danger">{error}</Banner>;
  if (!data) return null;
  const w = data.wallet;
  return (
    <div className="related-panel" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <StatGrid min={140}>
        <StatTile label="יתרה" icon="wallet" tone={w.lowBalance ? "warning" : "primary"} value={fmtCredits(w.remainingCredits)} />
        <StatTile label="נוצל החודש" icon="sparkle" value={fmtCredits(w.usedCredits)} sub={fmtUsd(w.costUsd)} />
        <StatTile label="נרכשו" icon="coin" tone="success" value={fmtCredits(w.purchasedCredits)} />
      </StatGrid>
      <Switch checked={enabled} onChange={setEnabled} label="AI פעיל בארגון" hint="כבוי = אף סוכן AI לא עובד בארגון הזה" />
      <Field label="הקצאה חודשית (קרדיטים)" hint="ריק = ללא הגבלה · 250 קרדיטים = $1">
        <TextInput ltr inputMode="numeric" value={allowance} onChange={(e) => setAllowance(e.target.value)} />
      </Field>
      <Field label="סף יתרה נמוכה" hint="ריק = בלי התראה">
        <TextInput ltr inputMode="numeric" value={low} onChange={(e) => setLow(e.target.value)} />
      </Field>
      <div>
        <Button variant="primary" icon="check" onClick={() => void save()} disabled={busy}>
          {busy ? "שומר…" : "שמירה"}
        </Button>
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <Field label="הענקה / תיקון (קרדיטים)" hint="שלילי מוריד מהיתרה שנרכשה">
          <TextInput ltr value={grant} onChange={(e) => setGrant(e.target.value)} style={{ maxWidth: 140 }} />
        </Field>
        <Field label="הערה">
          <TextInput value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <Button icon="plus" disabled={busy || !grant.trim()} onClick={() => void adjust()}>
          עדכון יתרה
        </Button>
      </div>
      <SimpleTable
        ariaLabel="תנועות בארנק"
        rows={data.transactions}
        rowKey={(t: Tx) => t.id}
        empty="אין תנועות"
        maxHeight={260}
        columns={[
          { key: "createdAt", label: "תאריך", render: (t: Tx) => fmtDate(t.createdAt) },
          { key: "kind", label: "סוג", render: (t: Tx) => TX_LABELS[t.kind] ?? t.kind },
          { key: "credits", label: "קרדיטים", align: "end", render: (t: Tx) => (t.kind === "allowance" ? "—" : fmtCredits(t.credits)) },
          { key: "note", label: "הערה", render: (t: Tx) => t.note ?? "" },
        ]}
      />
    </div>
  );
}
